import { useState, useEffect, useRef } from 'react'
import { preview as platformPreview } from '@carrent/platform'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { ticketsApi, carsApi } from '@carrent/api'
import { api } from '@carrent/api'

const CATEGORIES = [
  { value: 'technical', label: 'Техническая проблема', icon: '🔧' },
  { value: 'accident',  label: 'ДТП / повреждение',     icon: '🚨' },
  { value: 'documents', label: 'Документы',              icon: '📄' },
  { value: 'payment',   label: 'Оплата / финансы',       icon: '💳' },
  { value: 'other',     label: 'Другое',                 icon: '💬' },
]

const PRIORITIES = [
  { value: 'low',    label: 'Низкий',  color: '#6b7280' },
  { value: 'normal', label: 'Обычный', color: '#3b82f6' },
  { value: 'high',   label: 'Высокий', color: '#f59e0b' },
  { value: 'urgent', label: 'Срочный', color: '#ef4444' },
]

export default function CreateTicketPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const presetCarId = location.state?.carId

  const [subject, setSubject]   = useState('')
  const [body, setBody]         = useState('')
  const [category, setCategory] = useState('technical')
  const [priority, setPriority] = useState('normal')
  const [carId, setCarId]       = useState(presetCarId || '')
  const [cars, setCars]         = useState([])
  const [photos, setPhotos]     = useState([])
  const [loading, setLoading]   = useState(false)
  const [err, setErr]           = useState('')
  const fileRef                 = useRef()

  // Загружаем доступные машины и активную поездку
  useEffect(() => {
    api.get('/cars/available').then(r => setCars(r.data?.data || [])).catch(() => {})
    // Если есть активная поездка — подставим машину
    if (!presetCarId) {
      api.get('/trips/active').then(r => {
        const t = r.data?.data
        if (t?.car_id) setCarId(t.car_id)
      }).catch(() => {})
    }
  }, [])

  const handleFile = (e) => {
    const files = Array.from(e.target.files)
    setPhotos(p => [...p, ...files])
    e.target.value = ''
  }
  const removePhoto = (idx) => setPhotos(p => p.filter((_, i) => i !== idx))

  const submit = async () => {
    if (!subject.trim()) { setErr('Укажите тему обращения'); return }
    if (!body.trim())    { setErr('Опишите проблему'); return }
    setErr(''); setLoading(true)

    const fd = new FormData()
    fd.append('subject',  subject)
    fd.append('body',     body)
    fd.append('category', category)
    fd.append('priority', priority)
    if (carId) fd.append('car_id', carId)
    photos.forEach((f, i) => fd.append(`file_${i}`, f))

    try {
      const { data } = await ticketsApi.create(fd)
      navigate(`/support/${data.data.id}`)
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка создания обращения')
    } finally { setLoading(false) }
  }

  return (
    <div>
      <PageHeader title="Новое обращение" backTo="/support" />

      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>

        {/* Категория */}
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label">Категория</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {CATEGORIES.map(c => (
              <button key={c.value} type="button"
                onClick={() => setCategory(c.value)}
                style={{
                  padding: '8px', border: `1px solid ${category === c.value ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--r-sm)',
                  background: category === c.value ? 'rgba(59,130,246,0.1)' : 'transparent',
                  cursor: 'pointer', fontSize: '0.82rem', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 6,
                  color: category === c.value ? 'var(--accent)' : 'var(--text)',
                  fontWeight: category === c.value ? 700 : 400,
                }}>
                <span style={{ fontSize: '1rem' }}>{c.icon}</span>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Тема */}
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label">
            Тема <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <input className="input"
            placeholder="Кратко опишите проблему"
            maxLength={200}
            value={subject} onChange={e => setSubject(e.target.value)} />
        </div>

        {/* Текст */}
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label">
            Описание <span style={{ color: 'var(--danger)' }}>*</span>
          </label>
          <textarea className="input" rows={5}
            placeholder="Подробно опишите ситуацию..."
            value={body} onChange={e => setBody(e.target.value)}
            style={{ resize: 'vertical' }} />
        </div>

        {/* Машина */}
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label">Связанный автомобиль (опционально)</label>
          <select className="input" value={carId} onChange={e => setCarId(e.target.value)}>
            <option value="">— Без привязки —</option>
            {cars.map(c => (
              <option key={c.id} value={c.id}>
                {c.brand} {c.model} · {c.plate}
              </option>
            ))}
          </select>
        </div>

        {/* Приоритет */}
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label">Приоритет</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {PRIORITIES.map(p => (
              <button key={p.value} type="button"
                onClick={() => setPriority(p.value)}
                style={{
                  padding: '8px 4px', border: `1px solid ${priority === p.value ? p.color : 'var(--border)'}`,
                  borderRadius: 'var(--r-sm)',
                  background: priority === p.value ? `${p.color}18` : 'transparent',
                  cursor: 'pointer', fontSize: '0.78rem',
                  color: priority === p.value ? p.color : 'var(--text)',
                  fontWeight: priority === p.value ? 700 : 400,
                }}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Фото */}
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label">Фото (опционально)</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
            {photos.map((f, i) => (
              <div key={i} style={{ position: 'relative', aspectRatio: '4/3' }}>
                <img src={platformPreview.objectUrl(f)} alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover',
                    borderRadius: 'var(--r-sm)', border: '1px solid var(--border)' }} />
                <button type="button" onClick={() => removePhoto(i)} style={{
                  position: 'absolute', top: 3, right: 3,
                  width: 22, height: 22, borderRadius: '50%',
                  background: 'rgba(239,68,68,0.85)', color: '#fff',
                  border: 'none', cursor: 'pointer', fontSize: '0.75rem',
                }}>✕</button>
              </div>
            ))}
            <button type="button" onClick={() => fileRef.current?.click()}
              style={{
                aspectRatio: '4/3', border: '1px dashed var(--border)',
                borderRadius: 'var(--r-sm)', background: 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexDirection: 'column', gap: 4,
                color: 'var(--text-muted)',
              }}>
              <span style={{ fontSize: '1.5rem' }}>📷</span>
              <span style={{ fontSize: '0.72rem' }}>Добавить</span>
            </button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment"
            multiple style={{ display: 'none' }} onChange={handleFile} />
        </div>

        {err && <div className="alert alert--error">{err}</div>}

        <button className="btn btn--primary btn--full" style={{ height: 50 }}
          onClick={submit} disabled={loading}>
          {loading ? <span className="spinner" /> : '📤 Отправить обращение'}
        </button>
      </div>
    </div>
  )
}
